import { csvCell } from '~/utils/csvCell';

import { monthlyTable, popRecords, salesColumnLabel, salesTable } from './SupplementalViews';
import { config } from './config';
import type { MarketingPresentation } from './liveDataAdapter';
import {
  type Filters,
  attributionExportTables,
  buildTable,
  dimensions,
  performanceExportTable,
  productExportTable,
  productRows,
  tableMetrics,
  value,
} from './presentationModel';

type ExportTable = Array<Array<unknown>>;
type SourceScope = Record<string, unknown> & {
  period?: { comparison?: { from?: string; to?: string } | string | null };
};

const scopeLabels: Record<string, string> = {
  data_split_1: 'Data Split 1',
  data_split_2: 'Data Split 2',
  date_grain: 'Date cadence',
};

function exportCell(value: unknown) {
  return csvCell(typeof value === 'number' ? Number(value.toFixed(Math.abs(value) >= 1000 ? 2 : 4)) : value, 'Unknown');
}

function serialize(table: ExportTable) {
  return table.map((row) => row.map(exportCell).join(',')).join('\r\n');
}

/** Key/value scope rows written above every export, matching the Contribution Margin and KPI exports. */
export function scopeHeader(filters: Filters, model?: Pick<MarketingPresentation, 'sourceScope' | 'asOf'>): string {
  const request: SourceScope = model ? JSON.parse(model.sourceScope) : {};
  const comparison = request.period?.comparison;
  const selections = Object.entries(request).filter(
    ([key, values]) => Array.isArray(values) && !['country', 'attribution_model'].includes(key),
  ) as Array<[string, Array<string>]>;
  return serialize([
    ['Report', 'Performance Marketing'],
    ['View', filters.view],
    ['From', filters.from],
    ['To', filters.to],
    ...(typeof comparison === 'object' && comparison?.from
      ? [['Previous period', `${comparison.from} to ${comparison.to}`]]
      : []),
    ['Country', Array.isArray(request.country) ? request.country.join('; ') : filters.country],
    ['Attribution model', filters.attribution],
    ['Metric', filters.metric],
    ...selections.map(([key, values]) => [
      scopeLabels[key] ?? dimensions[key] ?? key,
      values.map((value) => dimensions[value] ?? value).join('; '),
    ]),
    ['Source', filters.view === 'Sales' ? 'lux-analytics-prod.analytics.sales_attribution' : config.source],
    ...(model ? [['Sydney as of', model.asOf]] : []),
    ...(filters.view === 'Product mix'
      ? [['Note', 'Orders can contain multiple products; shares overlap and must not be added to 100%']]
      : []),
  ]);
}

/** Download name carries the view, the filtered country and the dates. */
export function marketingCsvFilename(filters: Filters): string {
  const slug = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  const country = filters.country && filters.country !== 'All' ? `-${slug(filters.country)}` : '';
  return `performance-marketing-${slug(filters.view)}${country}-${filters.from}-${filters.to}.csv`;
}

/** One complete settled source population is shared by display, host and every export. */
export function marketingCSV(
  model: MarketingPresentation,
  filters: Filters,
  ready: boolean,
  identity?: string,
): string {
  return [scopeHeader(filters, model), ...marketingTables(model, filters, ready, identity).map(serialize)].join(
    '\r\n\r\n',
  );
}

function marketingTables(
  model: MarketingPresentation,
  filters: Filters,
  ready: boolean,
  identity?: string,
): Array<ExportTable> {
  if (!ready || !identity || model.identity !== identity) {
    throw new Error('Wait for complete current data before exporting.');
  }
  if (filters.view === 'Attribution') {
    return attributionExportTables(model.attribution, filters);
  }
  if (filters.view === 'Product mix') {
    return [productExportTable(productRows(model.summary))];
  }
  if (filters.view === 'Sales') {
    const { rows, columns } = salesTable(model, filters);
    return [
      [
        ['Data Split 1', 'Data Split 2', ...columns.map(salesColumnLabel)],
        ...rows.map((row) => [row.split_one, row.split_two, ...columns.map((column) => row[column])]),
      ],
    ];
  }
  if (filters.view === 'PoP') {
    const records = popRecords(model, filters);
    return [[Object.keys(records[0]), ...records.map((record) => Object.values(record))]];
  }
  if (filters.view === 'Monthly') {
    const table = monthlyTable(model);
    return [[['Year', 'Measure', ...table.months], ...table.rows.map((row) => [row.year, row.measure, ...row.values])]];
  }
  const total = {
    id: 'total',
    time: 'Total',
    first: 'Selected scope',
    second: '',
    values: Object.fromEntries(tableMetrics.map((metric) => [metric, value(model.summary, metric)])),
    costShare: 1,
  };
  return [
    performanceExportTable(
      [total, ...buildTable(filters.timeSplit === 'Date Cadence' ? model.daily : model.summary, filters)],
      filters,
    ),
  ];
}
